import { Controller } from "@hotwired/stimulus"

// Tally embed behaviours (https://developers.tally.so/widgets).
//
// 1. Dynamic height: a Tally iframe whose src carries `dynamicHeight=1` only
//    shrinks to its form once Tally's widget script (a layout head script,
//    see Public::TallyHelper#tally_embed_script_tag) has attached its
//    resizer. Without it the iframe keeps its placeholder height and leaves
//    dead space below the form.
//
// 2. Optional success swap: Tally embeds post a `message` event on window
//    whose data is a JSON string containing "Tally.FormSubmitted"
//    (https://developers.tally.so/widgets/events), with payload.formId
//    identifying the submitted form. When a `success` template target is
//    present, the message came from the embed's own origin, and the
//    submission's formId matches this embed's iframe src, the element's
//    content is swapped for the template — our own success state instead of
//    Tally's branded one. Usages without the target (the /contact support
//    form) keep Tally's native behaviour.
//
// Mount on the iframe itself for the resize only, or on a wrapper (iframe as
// `frame` target, plus the `success` template and a `message@window` action)
// for the swap.

interface TallyApi {
  loadEmbeds: () => void
}

declare global {
  interface Window {
    Tally?: TallyApi
  }
}

const SUBMITTED_EVENT = "Tally.FormSubmitted"

// https://tally.so/embed/<formId>?… — the last path segment is the form id.
const formIdFrom = (embed: URL): string => embed.pathname.split("/").pop() ?? ""

export default class extends Controller<HTMLElement> {
  static targets = ["frame", "success"]

  declare readonly frameTarget: HTMLIFrameElement
  declare readonly hasFrameTarget: boolean
  declare readonly successTarget: HTMLTemplateElement
  declare readonly hasSuccessTarget: boolean

  connect() {
    // A restoration visit renders Turbo's cached snapshot, which was cloned
    // while the resizer was attached — so the iframe arrives carrying both the
    // "already initialized" flag and a frozen inline height. loadEmbeds() skips
    // flagged iframes, so without clearing these the restored form keeps the
    // old page's height (visibly wrong on step 2, on validation errors, and on
    // the short thank-you screen).
    const frame = this.embedFrame
    if (frame) {
      delete frame.dataset.tallyEmbedWidgetInitialized
      frame.style.height = ""
    }

    // Whichever arrives second does the scan: the widget script scans the
    // page when it executes, and an iframe that appears afterwards (a Turbo
    // visit, or a body parsed after the async script ran) only gets its
    // resizer when we ask for a re-scan.
    window.Tally?.loadEmbeds()
  }

  // data-action="message@window->tally-embed#formSubmitted"
  formSubmitted(event: MessageEvent) {
    if (!this.hasSuccessTarget) return
    if (!this.isOwnSubmission(event)) return

    this.element.replaceChildren(this.successTarget.content.cloneNode(true))
  }

  private isOwnSubmission(event: MessageEvent): boolean {
    const embed = this.embedUrl
    // No embed URL means no form to answer for. Without this the formId
    // comparison below would be `undefined === undefined` and a payload
    // carrying no formId would swap the card.
    if (!embed) return false

    // Only Tally speaks for a Tally form. Any page can postMessage to a window
    // it opened, so the origin is what makes the formId scoping meaningful.
    if (event.origin !== embed.origin) return false

    // MessageEvent#data is `any`; anything can postMessage anything.
    const data: unknown = event.data
    if (typeof data !== "string" || !data.includes(SUBMITTED_EVENT)) {
      return false
    }

    // Fails closed for the same reason as `embed` above: an empty form id
    // would match a payload that carries none.
    const formId = formIdFrom(embed)
    if (!formId) return false

    try {
      const message = JSON.parse(data) as {
        event?: string
        payload?: { formId?: string }
      }
      return (
        message.event === SUBMITTED_EVENT && message.payload?.formId === formId
      )
    } catch {
      return false
    }
  }

  private get embedFrame(): HTMLIFrameElement | null {
    if (this.hasFrameTarget) return this.frameTarget
    return this.element instanceof HTMLIFrameElement ? this.element : null
  }

  // The embed src identifies the form (https://tally.so/embed/<formId>?…), so
  // the swap only ever answers its own form's submission.
  private get embedUrl(): URL | null {
    const frame = this.embedFrame
    if (!frame?.src) return null

    try {
      return new URL(frame.src)
    } catch {
      return null
    }
  }
}
